import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, CheckOutline, DuplicateOutline, Flex, IconButton, Tooltip, useToast } from '@bonusly/design-system';

import type { BizyUiBlockAction } from '../../../schemas/bizy-ui-blocks';
import { useBizyUiBlockActionDispatcher } from '../lib/ui-block-action-dispatcher';

/**
 * Per-action visual treatment. Primary-looking actions ship as solid,
 * secondary-looking actions ship as ghost. Order is "least destructive
 * → primary intent" so the button at the right edge is the expected
 * commit action (Edit in givebox / Insert draft / View post).
 */
const ACTION_VARIANTS: Record<BizyUiBlockAction['type'], { variant: 'solid' | 'ghost'; colorScheme: 'primary' | 'secondary' }> = {
	insert_into_draft: { variant: 'solid', colorScheme: 'primary' },
	send_now: { variant: 'ghost', colorScheme: 'primary' },
	open_in_givebox: { variant: 'solid', colorScheme: 'primary' },
	view_post: { variant: 'ghost', colorScheme: 'secondary' },
	dismiss: { variant: 'ghost', colorScheme: 'secondary' },
};

/**
 * Prefer `action.label` when the backend specified one; otherwise
 * fall back to a translated default per action type.
 */
function useActionLabel(action: BizyUiBlockAction): string {
	const { t } = useTranslation('admin_bizy');
	if (action.label) return action.label;
	switch (action.type) {
		case 'insert_into_draft':
			return t('uiBlocks.actions.insertIntoDraft');
		case 'send_now':
			return t('uiBlocks.actions.sendNow');
		case 'open_in_givebox':
			return t('uiBlocks.actions.openInGivebox');
		case 'view_post':
			return t('uiBlocks.actions.viewPost');
		case 'dismiss':
			return t('uiBlocks.actions.dismiss');
	}
}

/**
 * Action bar rendered at the bottom of a UI block card. Renders a
 * left-side copy-to-clipboard icon (when `copyText` is provided) and a
 * right-aligned row of action buttons.
 *
 * Copy is a pure client-side affordance — the text comes from the
 * card's payload, not an action declaration — so it doesn't round-trip
 * through the dispatcher. It lets the user grab the draft even when
 * they don't want Send now / Edit in givebox.
 *
 * Actions are sorted so the primary (solid) commit action lands on
 * the right, matching recognition / comment card mocks.
 */
export function UiBlockActionBar({ actions, copyText, areActionsDisabled = false }: { actions: readonly BizyUiBlockAction[]; copyText?: string; areActionsDisabled?: boolean }) {
	if (actions.length === 0 && !copyText) return null;

	// Sort so secondary/ghost actions come first, solid primary action lands on the right.
	const ordered = [...actions].sort((a, b) => {
		const aIsPrimary = ACTION_VARIANTS[a.type].variant === 'solid';
		const bIsPrimary = ACTION_VARIANTS[b.type].variant === 'solid';
		if (aIsPrimary === bIsPrimary) return 0;
		return aIsPrimary ? 1 : -1;
	});

	return (
		<Flex justify="space-between" align="center" gap={2} mt={3}>
			<Flex align="center">{copyText && <CopyButton text={copyText} />}</Flex>
			<Flex align="center" gap={2}>
				{ordered.map((action, index) => (
					<ActionButton key={`${action.type}-${index}`} action={action} isDisabled={areActionsDisabled} />
				))}
			</Flex>
		</Flex>
	);
}

/**
 * Small icon button that writes `text` to the clipboard and briefly
 * flips to a checkmark. No analytics (the user hasn't committed to any
 * action), no dispatcher (pure DOM side-effect). A failed clipboard
 * write surfaces a toast so the user can tell it didn't work.
 */
function CopyButton({ text }: { text: string }) {
	const { t } = useTranslation('admin_bizy');
	const toast = useToast();
	const [copied, setCopied] = useState(false);
	const handleCopy = () => {
		navigator.clipboard
			.writeText(text)
			.then(() => {
				setCopied(true);
				setTimeout(() => setCopied(false), 2000);
			})
			.catch(() => {
				toast({ title: t('feedback.copyFailed'), status: 'error', duration: 2000 });
			});
	};
	return (
		<Tooltip label={copied ? t('feedback.copied') : t('feedback.copy')} placement="top">
			<IconButton
				aria-label={t('feedback.copy')}
				icon={copied ? <CheckOutline boxSize={4} /> : <DuplicateOutline boxSize={4} />}
				size="sm"
				variant="ghost"
				colorScheme="secondary"
				color="text.softer"
				onClick={handleCopy}
			/>
		</Tooltip>
	);
}

function ActionButton({ action, isDisabled }: { action: BizyUiBlockAction; isDisabled: boolean }) {
	const dispatch = useBizyUiBlockActionDispatcher();
	const label = useActionLabel(action);
	const { variant, colorScheme } = ACTION_VARIANTS[action.type];
	return (
		<Button variant={variant} colorScheme={colorScheme} size="sm" onClick={() => dispatch(action)} isDisabled={isDisabled}>
			{label}
		</Button>
	);
}
